/**
 * Typed wrapper for the agent's `/api/timeseries` history surface
 * (see `apps/agent/src/routes/timeseries-router.ts`).
 *
 * Single route:
 *   GET /api/timeseries?deviceId=…&sensorId=…&from=…&to=…[&maxPoints=…][&bucketMs=…]
 *
 * Returns `{ ts, value }[]` already downsampled by the agent so the
 * front-end never has to thin the result.
 */
import { agentFetch } from './agent-fetch';
import { loadPublicEnv } from './env';

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

export interface TimeSeriesPoint {
  ts: number;
  value: number;
}

export interface TimeSeriesQuery {
  deviceId: string;
  sensorId: string;
  from: number;
  to: number;
  maxPoints?: number;
  bucketMs?: number;
}

function baseUrl(): string {
  return loadPublicEnv().agentDevUrl.replace(/\/+$/, '');
}

export async function fetchTimeSeries(
  q: TimeSeriesQuery,
): Promise<Result<TimeSeriesPoint[]>> {
  const params = new URLSearchParams({
    deviceId: q.deviceId,
    sensorId: q.sensorId,
    from: String(q.from),
    to: String(q.to),
  });
  if (q.maxPoints !== undefined) params.set('maxPoints', String(q.maxPoints));
  if (q.bucketMs !== undefined) params.set('bucketMs', String(q.bucketMs));
  try {
    const res = await agentFetch(`${baseUrl()}/api/timeseries?${params.toString()}`, {
      headers: { 'content-type': 'application/json' },
    });
    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // ignore
      }
      const msg =
        body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string'
          ? (body as { error: string }).error
          : `HTTP ${res.status} ${res.statusText}`;
      return { ok: false, error: msg };
    }
    const body = (await res.json()) as {
      ok: boolean;
      points?: TimeSeriesPoint[];
      error?: string;
    };
    if (!body.ok || !Array.isArray(body.points)) {
      return { ok: false, error: body.error ?? 'malformed response' };
    }
    return { ok: true, value: body.points };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}